import React from 'react';

import { AppealType } from '@repo/types';

import { MarkToBeAppealedPayload } from '../../types';
import { useMarkToBeAppealed } from '../../queries/mattersQuery';
import {
  MarkToBeAppealedModal as MarkToBeAppealedModalContent,
  type MarkToBeAppealedSubmitData,
} from '../common/to-be-appealed';

interface MarkToBeAppealedModalProps {
  isOpen: boolean;
  invoiceId: string;
  initialAppealType?: AppealType;
  onClose: (success?: boolean) => void;
}

/**
 * Modal wrapper for the matter-detail module that uses the useMarkToBeAppealed hook
 * and transforms the shared submit data into the MarkToBeAppealedPayload format.
 */
export const MarkToBeAppealedModal: React.FC<MarkToBeAppealedModalProps> = ({
  isOpen,
  invoiceId,
  initialAppealType,
  onClose,
}) => {
  const { mutateAsync: markToBeAppealed } = useMarkToBeAppealed(invoiceId);

  const handleSubmit = async (data: MarkToBeAppealedSubmitData) => {
    const amountToBeAppealed = data.feesCut + data.expensesCut;

    const payload: MarkToBeAppealedPayload = {
      appealType: data.appealType,
      formData: {
        remainingAmount: Number(amountToBeAppealed.toFixed(2)),
        comment: data.comment,
        feesCut: data.feesCut,
        expensesCut: data.expensesCut,
        promptPayment: data.promptPayment,
        bulkDiscount: data.bulkDiscount,
        administrativeFee: data.administrativeFee,
        files: data.files,
      },
    };

    if (data.appealType === AppealType.SUBSEQUENT) {
      payload.formData.deadline = data.deadline;
      payload.formData.attyDeadline = data.attyDeadline;
      payload.formData.timekeepers = data.timekeepers;
    } else {
      payload.formData.appealedFees = data.appealedFees;
      payload.formData.appealedExpenses = data.appealedExpenses;
    }

    await markToBeAppealed(payload);
  };

  const handleClose = (success?: boolean) => {
    onClose(success);
  };

  return (
    <MarkToBeAppealedModalContent
      isOpen={isOpen}
      invoiceId={invoiceId}
      initialAppealType={initialAppealType}
      onSubmit={handleSubmit}
      onClose={handleClose}
    />
  );
};

export default MarkToBeAppealedModal;
